import React from "react";
import { Box, Switch, Typography, createStyles, withStyles } from "@material-ui/core";
import { magicLinkColors, magicLinkRadius } from "./magicLinkTokens";

interface Props {
  label: React.ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  inputId?: string;
  classes: Record<string, string>;
}

const styles = (theme: any) =>
  createStyles({
    row: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      minHeight: 44,
      boxSizing: "border-box",
      padding: "0 12px",
      borderRadius: magicLinkRadius.card,
      border: `1px solid ${magicLinkColors.border}`,
      backgroundColor: magicLinkColors.option,
      cursor: "pointer",
      userSelect: "none",
      transition: "border-color 150ms ease",
      "&:hover": {
        borderColor: theme.palette.primary.main
      },
      "&:focus-visible": {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2
      }
    },
    active: {
      borderColor: theme.palette.primary.main
    },
    label: {
      fontFamily: "Nunito Sans",
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: "20px",
      color: magicLinkColors.text,
      padding: "12px 0"
    },
    labelActive: {
      color: magicLinkColors.textStrong
    },
    toggle: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      flexShrink: 0
    },
    state: {
      fontFamily: "Nunito Sans",
      fontSize: "12px",
      lineHeight: "16px",
      minWidth: 20,
      color: magicLinkColors.text
    },
    switchRoot: {
      width: 25,
      height: 14,
      padding: 0,
      overflow: "visible"
    },
    switchBase: {
      padding: 2,
      color: magicLinkColors.surface,
      "&$checked": {
        transform: "translateX(11px)",
        color: magicLinkColors.surface,
        "& + $track": {
          backgroundColor: theme.palette.primary.main,
          opacity: 1
        }
      }
    },
    checked: {},
    thumb: {
      width: 10,
      height: 10,
      boxShadow: "none"
    },
    track: {
      borderRadius: 7,
      backgroundColor: magicLinkColors.disabled,
      opacity: 1
    }
  });

const OptionToggle = ({ label, checked, onChange, inputId, classes }: Props) => {
  const toggle = () => onChange(!checked);
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggle();
    }
  };
  return (
    <Box
      role="switch"
      aria-checked={checked}
      tabIndex={0}
      onClick={toggle}
      onKeyDown={handleKeyDown}
      className={checked ? `${classes.row} ${classes.active}` : classes.row}
    >
      <Typography
        className={
          checked ? `${classes.label} ${classes.labelActive}` : classes.label
        }
      >
        {label}
      </Typography>
      <Box className={classes.toggle}>
        <Switch
          checked={checked}
          color="primary"
          disableRipple
          tabIndex={-1}
          onClick={(event) => event.stopPropagation()}
          onChange={toggle}
          inputProps={{ "aria-hidden": true, id: inputId }}
          classes={{
            root: classes.switchRoot,
            switchBase: classes.switchBase,
            checked: classes.checked,
            thumb: classes.thumb,
            track: classes.track
          }}
        />
        <Typography className={classes.state}>{checked ? "Yes" : "No"}</Typography>
      </Box>
    </Box>
  );
};

export default withStyles(styles)(OptionToggle);
